import {iFrameConnection} from "./IFrameConnection";
import {IFrameMessages} from "../../blink-sdk/messaging/IFrameMessages";
import {CurrencyStore} from "../State";
import {
    AUTHENTICATION_PANEL_TYPE, EMPTY_PANEL_TYPE,
    GATE_TYPE,
    PANEL_FOOTER_THEME,
    PANEL_FRAME,
    PANEL_TYPE,
    PANEL_VARIANT
} from "../../blink-sdk/Constants";
import {
    ensureArray,
    ensureInEnum,
    ensureNonRecursivePlainObject,
    ensurePlainObject,
    isHTTPSUrl,
    isValidFontFace
} from "../paywall/Security";
import {CREATE_PG_DEFAULT_OPTIONS} from "../../blink-sdk/CreatePGDefaultOptions";
import {BlinkGlobal, cleanObject} from "../UtilsLib";
import {Dispatchable} from "../../stem-core/src/base/Dispatcher";
import {iFrameMerchantService} from "./IframeMerchantService";


class IFrameState extends Dispatchable {
    iframeParams = {
        forceHideWallet: false,
        gateType: null,
        screen: null,
        allowUserToCoverFees: null,
        merchantPageUrl: "",
        merchantPageReferrerUrl: "",
        preferredEmail: null,
        paymentRequestStatus: null,
        activePanel: false,
        ...CREATE_PG_DEFAULT_OPTIONS,
    };

    init(iframeParams) {
        this.iframeParams = iframeParams;

        BlinkGlobal.iFrameState = this;

        iFrameConnection.addListener(IFrameMessages.UPDATE_APP_STATE, ({iframeParams}) => {
            this.iframeParams = iframeParams;
            this.dispatch("update");
        });
    }

    get activePanel() {
        return this.iframeParams.activePanel;
    }

    get forceHideWallet() {
        return this.iframeParams.forceHideWallet;
    }

    get preferredEmail() {
        return this.iframeParams.preferredEmail;
    }

    get userJourneyId() {
        return this.iframeParams.userJourneyId;
    }

    // These PG options get transferred from paywall to modal
    // when the flow switches iframe.
    // TODO: Probably transfer unevaluated context? Not sure.
    get transferredOptions() {
        return {
            pgId: this.iframeParams.pgId,
            userJourneyId: this.iframeParams.userJourneyId,
            metadata: this.iframeParams.metadata,
            onFlowCheckpoint: this.iframeParams.onFlowCheckpoint,
        };
    }

    get analyticsProperties() {
        return {
            pgId: this.iframeParams.pgId ?? 0,
            userJourneyId: this.iframeParams.userJourneyId,
        };
    }

    get metadata() {
        const metadata = cleanObject({
            ...this.iframeParams.metadata,
            ...this.analyticsProperties,
        });
        if (Object.keys(metadata).length === 0) {
            return null;
        }
        return metadata;
    }

    get panelType() {
        if (this.iframeParams.type === AUTHENTICATION_PANEL_TYPE) {
            return AUTHENTICATION_PANEL_TYPE;
        }
        if (this.iframeParams.type === EMPTY_PANEL_TYPE) {
            return EMPTY_PANEL_TYPE;
        }
        return ensureInEnum(this.iframeParams.type, PANEL_TYPE);
    }

    get variant() {
        return ensureInEnum(this.iframeParams.variant, PANEL_VARIANT);
    }

    get frame() {
        return ensureInEnum(this.iframeParams.frame, PANEL_FRAME);
    }

    get gateType() {
        return ensureInEnum(this.iframeParams.gateType, GATE_TYPE);
    }

    get gateStyle() {
        return ensurePlainObject(this.iframeParams.gateStyle);
    }

    get customGateStyle() {
        return ensurePlainObject(this.iframeParams.customGateStyle);
    }

    get subscriptionOffer() {
        const {subscriptionOffer} = this.iframeParams;
        return subscriptionOffer ? (Array.isArray(subscriptionOffer) ? subscriptionOffer : [subscriptionOffer]) : null;
    }

    get newsletter() {
        const {newsletter} = this.iframeParams;
        return newsletter ? (Array.isArray(newsletter) ? newsletter : [newsletter]) : null;
    }

    get dashboardTabs() {
        return ensureArray(this.iframeParams.dashboardTabs ?? iFrameMerchantService.getDefaultDashboardTabs());
    }

    get authSocialProviders() {
        return ensureArray(this.iframeParams.authSocialProviders);
    }

    get cancelButtonCustomStyle() {
        return ensurePlainObject(this.iframeParams.cancelButtonCustomStyle);
    }

    get theme() {
        return ensurePlainObject(this.iframeParams.theme);
    }

    get messages() {
        return ensureNonRecursivePlainObject(this.iframeParams.messages);
    }

    get panelContent() {
        return this.iframeParams.content;
    }

    get title() {
        return this.iframeParams.title;
    }

    get panelText() {
        return this.iframeParams.text;
    }

    get textStyle() {
        return ensurePlainObject(this.iframeParams.textStyle);
    }

    get panelButtonText() {
        return this.iframeParams.buttonText;
    }

    get footerText() {
        return this.iframeParams.footerText;
    }

    get footerSeparatorStyle() {
        return ensurePlainObject(this.iframeParams.footerSeparatorStyle);
    }

    get footerTextStyle() {
        return ensurePlainObject(this.iframeParams.footerTextStyle);
    }

    get footerTheme() {
        return ensureInEnum(this.iframeParams.footerTheme, PANEL_FOOTER_THEME);
    }

    get loginMessageStyle() {
        return ensurePlainObject(this.iframeParams.loginMessageStyle);
    }

    get allowUserToCoverFees() {
        // TODO: Make sure you support the fees checkbox while editing after some model update.
        return this.iframeParams.allowUserToCoverFees;
    }

    get actionButtonStyle() {
        return ensurePlainObject(this.iframeParams.actionButtonStyle);
    }

    get logoSrc() {
        return isHTTPSUrl(this.iframeParams.logoSrc) ? this.iframeParams.logoSrc : null;
    }

    get logoAlt() {
        return this.iframeParams.logoAlt;
    }

    get logoStyle() {
        return ensurePlainObject(this.iframeParams.logoStyle);
    }

    get fonts() {
        return ensureArray(this.iframeParams.fonts).filter(isValidFontFace);
    }

    get merchantPageUrl() {
        return this.iframeParams.merchantPageUrl;
    }

    get merchantPageReferrerUrl() {
        return this.iframeParams.merchantPageReferrerUrl;
    }

    get widgetVisible() {
        return this.iframeParams.visible;
    }

    get screen() {
        return this.iframeParams.screen;
    }

    get positionBottom() {
        return this.iframeParams.positionBottom;
    }

    get scrimClickCollapse() {
        return this.iframeParams.scrimClickCollapse;
    }

    get paymentDetails() {
        return this.iframeParams.paymentDetails;
    }

    get paymentRequestStatus() {
        return this.iframeParams.paymentRequestStatus;
    }

    get pgId() {
        return this.iframeParams.pgId;
    }

    isInlineDonationFlow() {
        return this.gateType === GATE_TYPE.popup && this.panelType === PANEL_TYPE.donation;
    }

    isInlineSubscriptionFlow() {
        return this.gateType === GATE_TYPE.popup && this.panelType === PANEL_TYPE.subscribe;
    }

    getPaymentDetailsCurrency() {
        // TODO(@deprecated): currencyIsoCode will be deprecated
        return CurrencyStore.getByIsoCode(this.paymentDetails.currencyIsoCode || this.paymentDetails.currencyCode)
            || iFrameMerchantService.getMerchant().getCurrency();
    }
}

export const iFrameState = new IFrameState();
